import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  CheckmarkCircle01Icon, 
  ArrowRight01Icon, 
  InformationCircleIcon,
  FlashIcon,
  Notification01Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import BankAutocomplete from '../components/BankAutocomplete';
import { api } from '../utils/api';

const Onboarding = () => {
  const { user, login } = useAuth();
  const { subscribe } = usePushNotifications();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [derivFullName, setDerivFullName] = useState('');
  
  const [formData, setOrderFormData] = useState({
    business_name: '',
    bank_name: '',
    account_number: '',
    account_name: ''
  });

  const isAgent = user?.role === 'agent';

  useEffect(() => {
    console.log('Onboarding Mount:', { user, step, isAgent });
    if (!user) return;

    const fetchDerivInfo = async () => {
      try {
        const response = await api.get('/deriv/my-accounts');
        const fullName = response.data?.deriv_account?.fullname || '';
        setDerivFullName(fullName);
        if (!isAgent) {
          setOrderFormData(prev => ({ ...prev, account_name: fullName }));
        }
      } catch (err) {
        console.warn('Failed to fetch deriv info for onboarding:', err);
      }
    };
    fetchDerivInfo();
  }, [user, isAgent]);

  if (!user) return <div className="p-20 text-center">Loading User...</div>;

  const handleInputChange = (e) => {
    setOrderFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (step === 1 && !isAgent) {
      setStep(3); // Skip business name for clients
    } else {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step === 3 && !isAgent) {
      setStep(1);
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        business_name: isAgent ? formData.business_name : (user?.name || user?.email.split('@')[0])
      };
      
      await api.post('/onboarding/complete', payload);
      
      // Update local auth state with new user data
      const currentToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      login({ ...user, is_onboarded: true, business_name: payload.business_name }, currentToken, !!localStorage.getItem('access_token'));
      
      setStep(4); // Move to Notification Step
    } catch (err) {
      alert(err.message || "Onboarding failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationRequest = async () => {
    await subscribe();
    setStep(5);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-soft-green/10 p-6 rounded-full inline-block mb-2 text-soft-green">
              <HugeiconsIcon icon={FlashIcon} size={48} />
            </div>
            <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Welcome to Safzi!</h1>
            <p className="text-muted-gray text-lg max-w-md mx-auto">Your Deriv account is linked. Let's finish setting up your profile so you can start trading.</p>
            <Button onClick={handleNext} size="xl" className="w-full mt-8">
              Let's Go <HugeiconsIcon icon={ArrowRight01Icon} size={24} className="ml-2" />
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <h2 className="text-2xl font-black text-charcoal dark:text-white">Business Identity</h2>
            <p className="text-muted-gray">This name will be visible to your clients. We recommend matching your payment agent name for consistency.</p>
            
            <div className="space-y-4 text-left">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Payment Agent Name</label>
                <input 
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="e.g. Swift Liquidity"
                  className="w-full px-6 py-5 bg-off-white dark:bg-gray-800 border-none rounded-3xl outline-none font-bold text-xl text-charcoal dark:text-white focus:ring-2 focus:ring-soft-green"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="secondary" onClick={handleBack} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
              <Button onClick={handleNext} disabled={!formData.business_name} className="flex-1 py-4 rounded-2xl font-black">Next</Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <h2 className="text-2xl font-black text-charcoal dark:text-white">Bank Details</h2>
            <p className="text-muted-gray mb-8">Add your primary bank account for {isAgent ? 'receiving' : 'receiving/sending'} funds.</p>
            
            {!isAgent && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-2xl flex gap-3 text-left border border-yellow-100 mb-6">
                <HugeiconsIcon icon={InformationCircleIcon} size={20} className="text-yellow-600 shrink-0" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200 font-medium">The account name must match your Deriv profile: <b>{derivFullName}</b>.</p>
              </div>
            )}

            <div className="space-y-4 text-left">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Bank Name</label>
                <BankAutocomplete 
                  value={formData.bank_name}
                  onChange={(val) => setOrderFormData({ ...formData, bank_name: val })}
                  placeholder="Search or type your bank"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Account Number</label>
                <input 
                  name="account_number" 
                  maxLength={10}
                  value={formData.account_number} 
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (val.length <= 10) {
                      setOrderFormData({ ...formData, account_number: val });
                    }
                  }}
                  placeholder="0123456789" 
                  className={`w-full px-5 py-4 bg-off-white dark:bg-gray-800 border rounded-2xl outline-none font-bold text-charcoal dark:text-white focus:ring-2 focus:ring-soft-green ${
                    formData.account_number.length > 0 && formData.account_number.length !== 10 ? 'border-red-500' : 'border-none'
                  }`}
                />
                {formData.account_number.length > 0 && formData.account_number.length !== 10 && (
                  <p className="text-[10px] text-red-500 font-bold mt-1 px-1">Must be exactly 10 digits.</p>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Account Name</label>
                <input 
                  name="account_name" 
                  value={formData.account_name} 
                  onChange={handleInputChange} 
                  readOnly={!isAgent}
                  placeholder="e.g. John Doe" 
                  className={`w-full px-5 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white focus:ring-2 focus:ring-soft-green ${!isAgent ? 'opacity-50 cursor-not-allowed' : ''}`} 
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="secondary" onClick={handleBack} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
              <Button 
                onClick={handleComplete} 
                disabled={loading || !formData.bank_name || formData.account_number.length !== 10 || !formData.account_name} 
                className="flex-1 py-4 rounded-2xl font-black"
              >
                {loading ? 'Finalizing...' : 'Save & Continue'}
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="bg-blue-500/10 p-8 rounded-full inline-block">
              <HugeiconsIcon icon={Notification01Icon} size={64} className="text-blue-500" />
            </div>
            <div>
              <h2 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Stay Updated</h2>
              <p className="text-muted-gray mt-2 text-lg">Enable push notifications to get instant alerts for new orders and payments.</p>
            </div>
            
            <div className="flex flex-col gap-3 max-w-sm mx-auto pt-4">
              <Button onClick={handleNotificationRequest} size="xl" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                Enable Notifications
              </Button>
              <Button variant="ghost" onClick={() => setStep(5)} className="w-full py-4 font-bold text-muted-gray hover:text-charcoal dark:hover:text-white">
                Skip for Now
              </Button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8 animate-in fade-in zoom-in duration-700">
            <div className="bg-soft-green/10 p-8 rounded-full inline-block animate-bounce">
              <HugeiconsIcon icon={CheckmarkCircle01Icon} size={64} className="text-soft-green" />
            </div>
            <div>
              <h2 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">You're All Set!</h2>
              <p className="text-muted-gray mt-2 text-lg">Your profile is configured and verified. You can now start using Safzi.</p>
            </div>
            <Button onClick={() => navigate(isAgent ? '/dashboard' : '/')} size="xl" className="w-full">
              Go to Dashboard
            </Button>
          </div>
        );

      default: return <div>Error: Unknown Step {step}</div>;
    }
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 max-w-xl mx-auto py-12">
      <div className="w-full text-center relative">
        {renderStep()}
      </div>
      
      {/* Progress Footer */}
      {step < 5 && (
        <div className="mt-8 flex gap-2 justify-center">
          {(isAgent ? [1, 2, 3, 4] : [1, 3, 4]).map(s => (
            <div key={s} className={`h-1.5 w-8 rounded-full transition-all duration-500 ${step === s ? 'bg-soft-green w-12' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Onboarding;
